import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';
import { create } from 'zustand';
import { cn } from '@/lib/cn';

type ToastTone = 'success' | 'info' | 'warn' | 'danger';

interface Toast {
  id: number;
  title: string;
  description?: string;
  tone: ToastTone;
  durationMs: number;
  href?: { to: string; label: string };
}

interface ToastStore {
  toasts: Toast[];
  push: (t: Omit<Toast, 'id'> & Partial<Pick<Toast, 'durationMs' | 'tone'>>) => void;
  dismiss: (id: number) => void;
}

let id = 0;

export const useToast = create<ToastStore>(set => ({
  toasts: [],
  push: t =>
    set(s => {
      id += 1;
      const toast: Toast = {
        id,
        tone: t.tone ?? 'success',
        durationMs: t.durationMs ?? 4500,
        title: t.title,
        description: t.description,
        href: t.href,
      };
      return { toasts: [...s.toasts, toast] };
    }),
  dismiss: tid => set(s => ({ toasts: s.toasts.filter(t => t.id !== tid) })),
}));

/** Helper for fire-and-forget toasts. */
export const toast = {
  success: (title: string, description?: string, href?: Toast['href']) =>
    useToast.getState().push({ title, description, tone: 'success', href, durationMs: 4500 }),
  info: (title: string, description?: string, href?: Toast['href']) =>
    useToast.getState().push({ title, description, tone: 'info', href, durationMs: 4500 }),
  warn: (title: string, description?: string, href?: Toast['href']) =>
    useToast.getState().push({ title, description, tone: 'warn', href, durationMs: 5500 }),
  danger: (title: string, description?: string, href?: Toast['href']) =>
    useToast.getState().push({ title, description, tone: 'danger', href, durationMs: 6500 }),
};

export function Toaster() {
  const toasts = useToast(s => s.toasts);
  return createPortal(
    <div className="fixed bottom-4 right-4 z-[150] flex flex-col items-end gap-2 pointer-events-none">
      {toasts.map(t => <ToastItem key={t.id} t={t} />)}
    </div>,
    document.body,
  );
}

function ToastItem({ t }: { t: Toast }) {
  const dismiss = useToast(s => s.dismiss);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLeaving(true), t.durationMs);
    const removeTimer = setTimeout(() => dismiss(t.id), t.durationMs + 200);
    return () => {
      clearTimeout(timer);
      clearTimeout(removeTimer);
    };
  }, [t.durationMs, t.id, dismiss]);

  const Icon = t.tone === 'success' ? CheckCircle2 : t.tone === 'warn' || t.tone === 'danger' ? AlertTriangle : Info;
  const toneCls =
    t.tone === 'success' ? 'text-[color:var(--success)] bg-[color:var(--success-soft)]' :
      t.tone === 'warn' ? 'text-[color:var(--warn)] bg-[color:var(--warn-soft)]' :
        t.tone === 'danger' ? 'text-[color:var(--danger)] bg-[color:var(--danger-soft)]' :
          'text-accent bg-[color:var(--accent-soft)]';

  return (
    <div
      className={cn(
        'pointer-events-auto w-80 max-w-[calc(100vw-2rem)] bg-bg-elevated border border-border rounded-lg shadow-pop overflow-hidden',
        'transition-all duration-200',
        leaving ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0 animate-fade-in',
      )}
      role="status"
    >
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <span className={cn('inline-flex w-6 h-6 rounded-md items-center justify-center shrink-0', toneCls)}>
          <Icon size={13} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-fg">{t.title}</div>
          {t.description && (
            <div className="text-xs text-fg-muted leading-snug mt-0.5">{t.description}</div>
          )}
          {t.href && (
            <a
              href={t.href.to}
              className="mt-1 inline-block text-xs text-accent font-medium hover:underline"
            >
              {t.href.label} →
            </a>
          )}
        </div>
        <button
          onClick={() => dismiss(t.id)}
          className="text-fg-subtle hover:text-fg w-5 h-5 inline-flex items-center justify-center rounded shrink-0"
          aria-label="Dismiss"
        >
          <X size={11} />
        </button>
      </div>
    </div>
  );
}
